import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "@/components/Reveal";
import { scrollToTarget } from "@/App";

const SERVICES = [
  {
    num: "01",
    title: "Comunicazione interna",
    desc: "Tenere tutte le risorse umane aggiornate su quanto riguarda l'azienda è un must imprescindibile. Studio e realizzo strumenti e momenti di comunicazione, tradizionali e innovativi, su misura per le esigenze della tua impresa.",
    tags: ["Strumenti tradizionali", "Canali innovativi", "Aggiornamento HR"],
    img: "https://images.pexels.com/photos/39190477/pexels-photo-39190477.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Giango analizza i contenuti di comunicazione interna",
  },
  {
    num: "02",
    title: "Eventi & convention aziendali",
    desc: "I momenti di socializzazione e condivisione sono ormai un must nella vita delle aziende. Realizzo eventi e convention ad hoc, tenendo conto delle caratteristiche di chi parteciperà, per essere sempre in linea con le loro aspettative.",
    tags: ["Convention", "Kick-off", "Momenti di condivisione"],
    img: "https://images.pexels.com/photos/9275222/pexels-photo-9275222.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Convention aziendale con pubblico e luci di palco",
  },
  {
    num: "03",
    title: "Team building esperienziale",
    desc: "Attività di team building efficaci migliorano la produttività, aumentano la motivazione, incoraggiano la collaborazione e creano fiducia e rispetto tra i collaboratori. Ideo attività create ad hoc per le esigenze specifiche della tua azienda.",
    tags: ["Collaborazione", "Motivazione", "Fiducia e rispetto"],
    img: "https://images.pexels.com/photos/7551180/pexels-photo-7551180.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Attività di team building all'aperto con la squadra",
  },
  {
    num: "04",
    title: "Video tradizionali & AI",
    desc: "La comunicazione interna ed esterna oggi non può prescindere dal video. Realizzo video con le tecnologie tradizionali e con l'intelligenza artificiale: massima flessibilità e un rapporto costo-benefici veramente alto.",
    tags: ["Produzione video", "Intelligenza artificiale", "Costo-beneficio"],
    img: "https://images.unsplash.com/photo-1625690303837-654c9666d2d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHx2aWRlbyUyMHByb2R1Y3Rpb24lMjBjYW1lcmElMjBmaWxtaW5nJTIwc3R1ZGlvfGVufDB8fHx8MTc5MDE1ODI4M3ww&ixlib=rb-4.1.0&q=85",
    alt: "Cinepresa professionale in studio tra luci blu e rosse",
  },
];

function ServiceCard({ service, index }) {
  return (
    <motion.article
      data-testid={`service-card-${index + 1}`}
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: (index % 2) * 0.12, ease: EASE }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-surface hover:border-ember/40 transition-colors duration-500"
    >
      <div className="relative overflow-hidden aspect-[16/10]">
        <img
          src={service.img}
          alt={service.alt}
          loading="lazy"
          className="h-full w-full object-cover img-duotone transition-all duration-700 group-hover:scale-105 group-hover:saturate-100"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" aria-hidden="true" />
        <span className="absolute top-4 left-4 font-mono text-xs text-ember bg-obsidian/70 backdrop-blur px-3 py-1.5 rounded-full border border-ember/30">
          /{service.num}
        </span>
      </div>

      <div className="flex flex-col gap-4 p-7 sm:p-8 flex-1">
        <h3 className="font-display font-bold text-xl sm:text-2xl text-ink group-hover:text-ember transition-colors duration-500">
          {service.title}
        </h3>
        <p className="text-zinc-400 leading-relaxed text-sm sm:text-base flex-1">
          {service.desc}
        </p>
        <div className="flex flex-wrap gap-2 pt-2">
          {service.tags.map((tag) => (
            <span
              key={tag}
              className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 border border-white/10 rounded-full px-3 py-1.5 group-hover:border-ember/40 transition-colors duration-500"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section id="servizi" className="relative py-24 sm:py-32 bg-surface/40">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <SectionHeading
          overline="I servizi"
          title={<>Creatività applicata, in quattro direzioni.</>}
          testId="services-title"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.num} service={s} index={i} />
          ))}
        </div>

        <Reveal delay={0.15} className="mt-14 text-center">
          <button
            onClick={() => scrollToTarget("#contatti")}
            className="group inline-flex items-center gap-2.5 border border-ember/50 text-ember font-semibold px-7 py-4 rounded-full hover:bg-ember hover:text-obsidian transition-all duration-300"
          >
            Raccontami la tua esigenza
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
